import * as v from 'valibot';

/**
 * Something a run learned, waiting for somebody to agree it is true.
 *
 * Every agent product that accumulates knowledge has the same failure mode: an
 * agent that writes to its own instructions is an agent whose instructions
 * nobody reviewed, and the second time it is wrong it is wrong with conviction.
 *
 * So knowledge here is a *proposal* and nothing else. It has no effect on any
 * run until a person accepts it, and accepting is a commit that bumps a skill
 * version — which means the thing that changed an agent's behaviour is visible
 * in a diff, revertible with `git revert`, and attributable to whoever pressed
 * the button. Deliberately the same shape as a staged write, because it is the
 * same bargain.
 */
export const KnowledgeState = v.picklist(['proposed', 'accepted', 'rejected', 'superseded']);
export type KnowledgeState = v.InferOutput<typeof KnowledgeState>;

export const KnowledgeProposal = v.object({
  id: v.string(),
  /** The skill this would be added to. Must already exist — see `listSkillNames`. */
  skillName: v.string(),
  /** The sentence that would be added. One claim, not a paragraph. */
  claim: v.pipe(v.string(), v.minLength(8), v.maxLength(600)),
  runId: v.nullable(v.string()),
  stepId: v.nullable(v.string()),
  /** Where it came from, so a reviewer can go and look rather than take it on faith. */
  evidence: v.nullable(v.string()),
  state: KnowledgeState,
  proposedBy: v.nullable(v.string()),
  /** Resolved on read, not copied in at write time. See `AuditEvent.actorEmail`. */
  proposedByEmail: v.optional(v.nullable(v.string())),
  createdAt: v.string(),
  decidedBy: v.nullable(v.string()),
  decidedAt: v.nullable(v.string()),
  commitSha: v.nullable(v.string()),
});
export type KnowledgeProposal = v.InferOutput<typeof KnowledgeProposal>;

/**
 * What a step passes to `reef.learn()`.
 *
 * Bounded lengths are not tidiness. A claim long enough to be a paragraph is a
 * claim a reviewer skims, and a skim is an approval that means nothing — which
 * is exactly the failure this whole mechanism exists to avoid.
 */
export const KnowledgeWrite = v.object({
  skill: v.string(),
  claim: v.pipe(v.string(), v.minLength(8), v.maxLength(600)),
  evidence: v.optional(v.pipe(v.string(), v.maxLength(4000))),
});
export type KnowledgeWrite = v.InferOutput<typeof KnowledgeWrite>;

/**
 * How an accepted claim is written into a skill file.
 *
 * Appended under a heading rather than merged into the prose, so the reviewed
 * sentence stays the reviewed sentence: a model rewriting the surrounding
 * paragraph to accommodate it would put words nobody approved into a document
 * that changes how every future run behaves.
 */
export const LEARNED_HEADING = '## Learned';

export function appendClaim(skillBody: string, claim: string, source: string): string {
  const entry = `- ${claim.trim()} _(${source})_`;
  if (skillBody.includes(LEARNED_HEADING)) {
    return `${skillBody.trimEnd()}\n${entry}\n`;
  }
  return `${skillBody.trimEnd()}\n\n${LEARNED_HEADING}\n\n${entry}\n`;
}
